// =============================================================================
// features/sales/campaigns/wizard/steps/Step3Hygiene.tsx — Etapa 3: prévia de
// higiene (F35-S08, doc 26 §4.2/§6.6). CRÍTICO: a contagem vem SEMPRE da API
// (`POST /audience/preview` → `POST /audience/apply`, mesma função
// `classifyAudience` dos dois lados) — nada é recalculado aqui.
//
// `import` é a única fonte sem prévia não-destrutiva (a API não oferece —
// ver aviso em hooks/sales/useCampaignWizard.ts): o aviso fica visível ANTES
// do gestor confirmar, nunca escondido atrás de um botão "prévia" que na
// prática já grava.
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import {
  useApplyAudience,
  useImportBatch,
  useImportCampaignMembers,
  usePreviewAudience,
} from '../../../../../hooks/sales/useCampaignWizard';
import type {
  AudiencePreviewResponse,
  CampaignMembersImportResponse,
} from '../../../../../hooks/sales/useCampaignWizard';
import { buildAudienceSource } from '../audience';
import { HygieneCountsGrid } from '../components/HygieneCountsGrid';
import { ImportBatchNotice } from '../components/ImportBatchNotice';
import { WizardStepShell } from '../components/WizardStepShell';
import type { AudienceState, HygieneResultState } from '../types';

export interface Step3HygieneProps {
  campaignId: string;
  audience: AudienceState;
  onBack: () => void;
  onContinue: (result: HygieneResultState) => void;
}

function importResponseToHygiene(res: CampaignMembersImportResponse): HygieneResultState {
  return {
    requested: res.requested,
    counts: {
      new: res.added,
      reused: res.reused,
      duplicate: 0,
      invalid: res.rejected,
      blocked: res.blocked,
      alreadyPresent: res.alreadyPresent,
    },
    willMaterialize: res.added + res.reused,
    applied: true,
  };
}

function previewToHygiene(res: AudiencePreviewResponse): HygieneResultState {
  return {
    requested: res.requested,
    counts: res.counts,
    willMaterialize: res.willMaterialize,
    applied: false,
  };
}

export function Step3Hygiene({
  campaignId,
  audience,
  onBack,
  onContinue,
}: Step3HygieneProps): React.JSX.Element {
  const { mutateAsync: preview, isPending: isPreviewing } = usePreviewAudience();
  const { mutateAsync: apply, isPending: isApplying } = useApplyAudience();
  const { mutateAsync: importMembers, isPending: isImporting } = useImportCampaignMembers();

  const [result, setResult] = React.useState<HygieneResultState | null>(null);
  const [previewedKey, setPreviewedKey] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { source, blockedReason } = buildAudienceSource(audience);
  const sourceKey = source ? JSON.stringify(source) : null;
  const isStale = audience.kind !== 'import' && previewedKey !== sourceKey;

  const importBatchQuery = useImportBatch(
    audience.kind === 'import' ? audience.importBatchId || null : null,
  );

  async function handlePreview(): Promise<void> {
    if (!source) return;
    setErrorMessage(null);
    try {
      const res = await preview({ id: campaignId, source });
      setResult(previewToHygiene(res));
      setPreviewedKey(sourceKey);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Não foi possível calcular a prévia.');
    }
  }

  async function handleContinue(): Promise<void> {
    setErrorMessage(null);
    try {
      if (audience.kind === 'import') {
        if (!audience.importBatchId) {
          setErrorMessage('Informe o id do lote de importação.');
          return;
        }
        const res = await importMembers({ id: campaignId, importBatchId: audience.importBatchId });
        const hygiene = importResponseToHygiene(res);
        setResult(hygiene);
        onContinue(hygiene);
        return;
      }

      if (!source) {
        setErrorMessage(blockedReason ?? 'Revise o público na etapa anterior.');
        return;
      }
      const res = await apply({ id: campaignId, source });
      const hygiene: HygieneResultState = {
        requested:
          res.counts.new +
          res.counts.reused +
          res.counts.duplicate +
          res.counts.invalid +
          res.counts.blocked +
          res.counts.alreadyPresent,
        counts: res.counts,
        willMaterialize: res.materializedCount,
        applied: true,
      };
      setResult(hygiene);
      onContinue(hygiene);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Não foi possível aplicar o público.');
    }
  }

  const isPending = isPreviewing || isApplying || isImporting;

  return (
    <WizardStepShell
      title="Higiene do público"
      description="O que vai entrar na carteira desta campanha, com o motivo de cada exclusão — antes de qualquer gravação em definitivo."
      onBack={onBack}
      onContinue={() => void handleContinue()}
      continueLabel={result?.applied ? 'Continuar' : 'Aplicar e continuar'}
      continueDisabled={
        audience.kind === 'import'
          ? !audience.importBatchId
          : blockedReason !== null || (isStale && result === null)
      }
      isPending={isPending}
      errorMessage={errorMessage}
    >
      {audience.kind !== 'import' && (
        <div className="flex flex-col gap-4">
          {blockedReason && (
            <p className="font-sans text-sm text-warning" role="status">
              {blockedReason}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handlePreview()}
              disabled={blockedReason !== null || isPending}
            >
              {isPreviewing
                ? 'Classificando…'
                : isStale && result
                  ? 'Atualizar prévia'
                  : 'Ver prévia'}
            </Button>
            {result && !isStale && (
              <span className="font-sans text-xs text-ink-3">
                {result.applied
                  ? 'Público aplicado à carteira.'
                  : 'Prévia — nada foi gravado ainda.'}
              </span>
            )}
            {isStale && result && (
              <span className="font-sans text-xs text-warning">
                O público mudou desde a última prévia — atualize antes de continuar.
              </span>
            )}
          </div>
        </div>
      )}

      {audience.kind === 'import' && (
        <ImportBatchNotice
          importBatchId={audience.importBatchId}
          batch={importBatchQuery.data}
          isError={importBatchQuery.isError}
        />
      )}

      {result && (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <p className="font-sans text-sm font-semibold text-ink">
              {result.requested.toLocaleString('pt-BR')} candidato(s) avaliados
            </p>
            <p className="font-mono text-sm font-semibold text-verde">
              {result.willMaterialize.toLocaleString('pt-BR')} vão para a carteira
            </p>
          </div>
          <HygieneCountsGrid counts={result.counts} />
        </div>
      )}
    </WizardStepShell>
  );
}
